import React from 'react';
import { Box, Stack, TextField, MenuItem } from '@mui/material';
import { LocationOnOutlined } from '@mui/icons-material';
import { SectionHeader, SectionPaper } from '../../../../common/components/styled-ui-elements';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { updateComplianceFacility } from '../../../state';
import {
  useGetCentersQuery,
  useGetHstCentersQuery,
  useGetCpsiCentersQuery,
} from '../../../../../services/DimensionService/dimensionApi';
import type { EnumView } from '../../../../../services/DimensionService/types/dimensionTypes';

interface FacilitySectionProps {
  /** Facility (Center) is the Cosmos partition key and is immutable once the item is created. */
  readOnly?: boolean;
}

interface CenterOption {
  value: string;
}

// Facility Type is never chosen directly — it's derived from which center grouping the selected
// center falls under (mirrors ComplianceItem.IsHospitalFacility/IsAscFacility on the backend).
// Deriving it here too means it appears immediately on selection instead of only after the item
// is saved and re-fetched.
const deriveFacilityType = (
  selectedCenter: string,
  hstCenters: CenterOption[],
  cpsiCenters: CenterOption[],
): string => {
  if (cpsiCenters.some(c => c.value === selectedCenter)) return 'Hospital';
  if (hstCenters.some(c => c.value === selectedCenter)) return 'Surgery Center';
  return '';
};

/**
 * CenterField - editable center dropdown, or a read-only display once the item exists (facility
 * is immutable after creation).
 */
const CenterField: React.FC<{
  readOnly: boolean;
  center: string;
  centerName: string;
  centers: EnumView[];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ readOnly, center, centerName, centers, onChange }) => {
  if (readOnly) {
    return (
      <TextField
        label="Center"
        size="small"
        fullWidth
        value={centerName || ''}
        InputProps={{ readOnly: true }}
      />
    );
  }
  return (
    <TextField
      label="Center"
      size="small"
      required
      select
      fullWidth
      value={center || ''}
      onChange={onChange}
    >
      <MenuItem value="">
        <em>Select Center</em>
      </MenuItem>
      {centers.map((c) => (
        <MenuItem key={c.value} value={c.value}>
          {c.name}
        </MenuItem>
      ))}
    </TextField>
  );
};

/**
 * FacilitySection - Compliance facility/center field
 * Editable only when creating a new item; read-only once created (facility is immutable, see
 * research.md #1 — facility type itself is always derived, never stored or chosen directly).
 */
export const FacilitySection: React.FC<FacilitySectionProps> = ({ readOnly = false }) => {
  const dispatch = useAppDispatch();
  const { currentComplianceItem } = useAppSelector(state => state.compliance);
  const { data: centers = [] } = useGetCentersQuery();
  const { data: hstCenters = [] } = useGetHstCentersQuery();
  const { data: cpsiCenters = [] } = useGetCpsiCentersQuery();

  const center = currentComplianceItem?.center ?? '';
  const centerName = currentComplianceItem?.centerName ?? '';
  const storedFacilityType = currentComplianceItem?.facilityType ?? '';
  const facilityType = deriveFacilityType(center, hstCenters, cpsiCenters) || storedFacilityType;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateComplianceFacility({ center: event.target.value }));
  };

  return (
    <Box>
      <SectionHeader
        icon={<LocationOnOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />}
        title="Facility"
      />
      <SectionPaper>
        <Stack direction="row" gap={2}>
          <CenterField
            readOnly={readOnly}
            center={center}
            centerName={centerName}
            centers={centers}
            onChange={handleChange}
          />
          <TextField
            label="Facility Type"
            size="small"
            fullWidth
            value={facilityType}
            helperText="Derived from the selected center"
            InputProps={{ readOnly: true }}
          />
        </Stack>
      </SectionPaper>
    </Box>
  );
};

export default FacilitySection;
